import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { formatClock, formatRemaining } from './sonosPlaybackUtils';

interface TrackProgressSliderProps {
  position: number | null;
  duration: number | null;
  disabled?: boolean;
  onSeek: (seconds: number) => void;
}

const TrackProgressSlider: React.FC<TrackProgressSliderProps> = ({
  position,
  duration,
  disabled,
  onSeek,
}) => {
  const max = duration && duration > 0
    ? duration
    : Math.max((position ?? 0) + 120, 180);
  const [localPos, setLocalPos] = useState(position ?? 0);
  const draggingRef = useRef(false);
  const ignorePropUntilRef = useRef(0);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (draggingRef.current) return;
    if (Date.now() < ignorePropUntilRef.current) return;
    setLocalPos(position ?? 0);
  }, [position]);

  const commitSeek = useCallback((sec: number) => {
    const clamped = Math.max(0, Math.min(max, Math.round(sec)));
    ignorePropUntilRef.current = Date.now() + 12000;
    setLocalPos(clamped);
    onSeek(clamped);
  }, [max, onSeek]);

  const posFromClientX = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return localPos;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return localPos;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * max;
  }, [localPos, max]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
    setLocalPos(posFromClientX(e.clientX));
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setLocalPos(posFromClientX(e.clientX));
  };

  const finishDrag = (clientX: number) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    commitSeek(posFromClientX(clientX));
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    finishDrag(e.clientX);
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    if (disabled || draggingRef.current) return;
    e.stopPropagation();
    commitSeek(posFromClientX(e.clientX));
  };

  const pct = max > 0 ? Math.min(100, (localPos / max) * 100) : 0;

  return (
    <Box
      data-audio-no-swipe=""
      sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', touchAction: 'none' }}
    >
      <Typography sx={{ color: '#888', fontSize: 11, minWidth: 32, fontVariantNumeric: 'tabular-nums' }}>
        {formatClock(localPos)}
      </Typography>
      <Box
        ref={trackRef}
        onClick={handleTrackClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        sx={{
          flex: 1,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          cursor: disabled ? 'default' : 'pointer',
          touchAction: 'none',
          opacity: disabled ? 0.45 : 1,
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.25)' }}>
          <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, borderRadius: 2, backgroundColor: '#fff' }} />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: `${pct}%`,
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#fff',
              transform: 'translate(-50%, -50%)',
              boxShadow: draggingRef.current ? '0 0 0 8px rgba(255,255,255,0.12)' : 'none',
            }}
          />
        </Box>
      </Box>
      <Typography sx={{ color: '#888', fontSize: 11, minWidth: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {duration != null && duration > 0
          ? formatRemaining(Math.max(0, duration - localPos))
          : '-0:00'}
      </Typography>
    </Box>
  );
};

export default TrackProgressSlider;
